import { useEffect, useState } from "react";
import { useToast } from "@hightouch/htui";
import {
  type AmpLibraryFileFragment,
  useAmpLibrarySetFavoriteMutation,
} from "src/graphql";

/** Saves a file's favorite for the current user and toasts the result. `save` resolves to whether it saved. */
export function useSaveFileFavorite() {
  const { toast } = useToast();
  const setFavorite = useAmpLibrarySetFavoriteMutation();
  const save = async (fileId: string, favorite: boolean) => {
    try {
      await setFavorite.mutateAsync({ id: fileId, favorite });
      toast({
        id: "file-favorite",
        variant: "success",
        title: favorite ? "Added to favorites" : "Removed from favorites",
      });
      return true;
    } catch (error) {
      toast({
        id: "file-favorite",
        variant: "error",
        title: "Couldn't update favorite",
        message: error instanceof Error ? error.message : "Try again.",
      });
      return false;
    }
  };
  // Callers wait for a save to settle before the next, so a favorite and an unfavorite never race.
  return { save, isSaving: setFavorite.isLoading };
}

/** A file's favorite for the current user, flipped on click and held until the refetched file agrees. */
export function useOptimisticFavorite(
  file: Pick<AmpLibraryFileFragment, "id" | "isFavorite">,
) {
  const { save, isSaving } = useSaveFileFavorite();
  const [pending, setPending] = useState<boolean>();
  const isFavorite = pending ?? file.isFavorite;

  // Only a refetched value confirms a click; matching the value from before the save would let an older refetch flip the heart back.
  useEffect(() => {
    setPending((current) =>
      current === file.isFavorite ? undefined : current,
    );
  }, [file.isFavorite]);

  const toggle = async () => {
    if (isSaving) return;
    const favorite = !isFavorite;
    setPending(favorite);
    if (!(await save(file.id, favorite))) setPending(undefined);
  };

  return { isFavorite, toggle };
}
